import React, { useState, useEffect, useRef } from 'react';
import { Pencil, Save, X, MapPin, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { updateCustomerCompanyDetailsCustomerCompanyManager } from '../../../../services/staffingService';

function AddressAutocomplete({ value, onPlaceSelected, onClear }) {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState(value || '');

  useEffect(() => { setInputValue(value || ''); }, [value]);

  useEffect(() => {
    if (!window.google?.maps?.places) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'address_components', 'geometry'],
      componentRestrictions: { country: 'us' },
    });
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      const comps = place.address_components || [];
      const data = { formatted_address: place.formatted_address, latitude: place.geometry.location.lat(), longitude: place.geometry.location.lng(), city: '', state: '', zipcode: '' };
      comps.forEach(c => {
        if (c.types.includes('locality')) data.city = c.long_name;
        if (c.types.includes('administrative_area_level_1')) data.state = c.short_name;
        if (c.types.includes('postal_code')) data.zipcode = c.long_name;
      });
      setInputValue(place.formatted_address);
      onPlaceSelected(data);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [onPlaceSelected]);

  return (
    <div className="relative">
      <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder="Search business address…"
        className="w-full rounded-lg border border-gray-200 pl-8 pr-8 py-2 text-sm focus:outline-none focus:border-[#404880] bg-white"
      />
      {inputValue && (
        <button type="button" onClick={() => { setInputValue(''); onClear(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          <X size={14} />
        </button>
      )}
    </div>
  );
}

function ReadField({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-500 mb-0.5">{label}</p>
      <p className="text-sm text-gray-900">{value || <span className="italic text-gray-400">—</span>}</p>
    </div>
  );
}

const Legal = ({ customerCompany, setCustomerCompany }) => {
  const [editing, setEditing] = useState(false);
  const [saving,  setSaving]  = useState(false);

  const toForm = (c) => ({
    companyLegalName:    c.companyLegalName    || '',
    companyFriendlyName: c.companyFriendlyName || '',
    supportEmail:        c.supportEmail        || '',
    supportPhone:        c.supportPhone        || '',
    vatTaxId:            c.vatTaxId            || '',
    legalAddress:        c.legalAddress        || '',
    geoLocation:         c.geoLocation         || null,
    contactPersonName:   c.contact?.contactPersonName || '',
    contactPhone:        c.contact?.phone      || '',
    contactEmail:        c.contact?.email      || '',
  });

  const [form, setForm] = useState(() => toForm(customerCompany));
  useEffect(() => { setForm(toForm(customerCompany)); }, [customerCompany]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleAddressSelected = (data) => {
    setForm(p => ({ ...p, legalAddress: data.formatted_address, geoLocation: { lat: data.latitude, lng: data.longitude } }));
  };

  const handleSave = async () => {
    if (!form.companyLegalName.trim()) return toast.error('Legal business name is required');
    setSaving(true);
    try {
      await updateCustomerCompanyDetailsCustomerCompanyManager(customerCompany._id, {
        companyLegalName:    form.companyLegalName,
        companyFriendlyName: form.companyFriendlyName,
        supportEmail:        form.supportEmail,
        supportPhone:        form.supportPhone,
        vatTaxId:            form.vatTaxId,
        legalAddress:        form.legalAddress,
        geoLocation:         form.geoLocation,
        contactPersonName:   form.contactPersonName,
        phone:               form.contactPhone,
        email:               form.contactEmail,
      });
      setCustomerCompany(prev => ({
        ...prev,
        companyLegalName:    form.companyLegalName,
        companyFriendlyName: form.companyFriendlyName,
        supportEmail:        form.supportEmail,
        supportPhone:        form.supportPhone,
        vatTaxId:            form.vatTaxId,
        legalAddress:        form.legalAddress,
        geoLocation:         form.geoLocation,
        contact: { contactPersonName: form.contactPersonName, phone: form.contactPhone, email: form.contactEmail },
      }));
      setEditing(false);
      toast.success('Company details updated');
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Legal & Compliance</h3>
          {!editing ? (
            <button onClick={() => setEditing(true)}
              className="flex items-center gap-1.5 text-xs text-[#404880] hover:underline">
              <Pencil size={12} /> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button onClick={() => { setForm(toForm(customerCompany)); setEditing(false); }}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
                <X size={12} /> Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1 rounded-lg bg-[#404880] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
              </button>
            </div>
          )}
        </div>

        {!editing ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ReadField label="Legal Business Name"  value={form.companyLegalName} />
            <ReadField label="Friendly Name"         value={form.companyFriendlyName} />
            <ReadField label="Support Email"         value={form.supportEmail} />
            <ReadField label="Support Phone"         value={form.supportPhone} />
            <ReadField label="VAT / Tax ID"          value={form.vatTaxId} />
            <ReadField label="Business Address"      value={form.legalAddress} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Legal Business Name <span className="text-red-500">*</span></label>
              <input value={form.companyLegalName} onChange={e => set('companyLegalName', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#404880]" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Friendly Name</label>
              <input value={form.companyFriendlyName} onChange={e => set('companyFriendlyName', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#404880]" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Support Email</label>
              <input type="email" value={form.supportEmail} onChange={e => set('supportEmail', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#404880]" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Support Phone</label>
              <input type="tel" value={form.supportPhone} onChange={e => set('supportPhone', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#404880]" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">VAT / Tax ID</label>
              <input value={form.vatTaxId} onChange={e => set('vatTaxId', e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#404880]" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Business Address</label>
              <AddressAutocomplete
                value={form.legalAddress}
                onPlaceSelected={handleAddressSelected}
                onClear={() => set('legalAddress', '')}
              />
              {form.geoLocation?.lat && (
                <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-600">
                  <MapPin size={10} /> Coordinates captured
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-gray-900">Primary Contact</h3>
        {!editing ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <ReadField label="Name"  value={form.contactPersonName} />
            <ReadField label="Phone" value={form.contactPhone} />
            <ReadField label="Email" value={form.contactEmail} />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {[
              { label: 'Name',  key: 'contactPersonName', type: 'text' },
              { label: 'Phone', key: 'contactPhone',       type: 'tel' },
              { label: 'Email', key: 'contactEmail',       type: 'email' },
            ].map(({ label, key, type }) => (
              <div key={key}>
                <label className="block text-xs text-gray-500 mb-1">{label}</label>
                <input type={type} value={form[key]} onChange={e => set(key, e.target.value)}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#404880]" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Legal;
